import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

// Imperativer Bestätigungsdialog — ein echtes Modal (derselbe shadcn-`Dialog`
// wie der Verlassen-Schutz), KEIN Toast. Statt jeden Lösch-Button mit eigenem
// open-State zu verdrahten, liefert `useConfirm()` eine Funktion, die ein
// Promise<boolean> zurückgibt:
//
//   const confirm = useConfirm();
//   if (await confirm({ title: "Produkt löschen?", confirmLabel: "Löschen" }))
//     del.mutate(...);
//
// Der Provider hängt einmal in der jeweiligen App (`App.tsx`) und rendert genau
// EIN Dialog für die ganze Oberfläche.

type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  /** Beschriftung des Bestätigen-Buttons (Default: „Bestätigen"). */
  confirmLabel?: string;
  /** Beschriftung des Abbrechen-Buttons (Default: „Abbrechen"). */
  cancelLabel?: string;
  /** Bestätigen-Button als „destructive" einfärben (z. B. Löschen). Default: true. */
  destructive?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  // Resolver des laufenden confirm()-Aufrufs — überlebt Re-Renders.
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={options !== null}
        onOpenChange={(open) => {
          // Schließen über Escape / Klick außerhalb = Abbrechen.
          if (!open) settle(false);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{options?.title}</DialogTitle>
            {options?.description ? (
              <DialogDescription>{options.description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => settle(false)}>
              {options?.cancelLabel ?? "Abbrechen"}
            </Button>
            <Button
              variant={options?.destructive === false ? "default" : "destructive"}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? "Bestätigen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/** Liefert eine `confirm(options) => Promise<boolean>`-Funktion. Setzt den
 *  `ConfirmProvider` voraus (in `App` montiert). */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx)
    throw new Error("useConfirm muss innerhalb von <ConfirmProvider> genutzt werden");
  return ctx;
}
